// Search rows carry neither coordinates nor a postcode, so a selection always
// needs a second lookup by id - and the two terminal row types resolve at
// different endpoints, so the row's own `type` decides which.

import { utm32ToWgs84 } from "./utm32";
import { normalizeQuery } from "./normalize";

const DEFAULT_BASE_URL = "https://adressevaelger.dk";
const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_RETRIES = 1;

const RESOURCE_BY_TYPE = {
    adresse: "adresser",
    husnummer: "husnumre"
};

const REFINEMENT_TYPES = ["navngivenvejpostnummer", "vejnavn"];

function isTerminalRow(row) {
    return Boolean(row) && Object.prototype.hasOwnProperty.call(RESOURCE_BY_TYPE, row.type);
}

function isRefinementRow(row) {
    return Boolean(row) && REFINEMENT_TYPES.indexOf(row.type) !== -1;
}

// "Industrigrenen 15, st., 2635 Ishøj" -> head "Industrigrenen 15, st."
// A supplementary town name stays with the head ("Rudholmvej 4, Søhale"),
// matching what DAWA's forslagstekst handling produced.
function parseDesignation(designation) {
    const text = String(designation == null ? "" : designation);
    const match = text.match(/^(.*),\s*(\d{4})\s+([^,]+)$/);
    return match
        ? { head: match[1], zip: match[2], city: match[3] }
        : { head: text, zip: null, city: null };
}

function stripPostalTail(designation) {
    return parseDesignation(designation).head;
}

// `/adresser/{id}` nests the house number; `/husnumre/{id}` is one directly.
function mapAddress(response) {
    if (!response) return null;

    const addressNode = response.adresse || null;
    const houseNumber = addressNode ? addressNode.husnummer : response.husnummer;
    if (!houseNumber) return null;

    const designation = addressNode
        ? addressNode.adressebetegnelse
        : houseNumber.adgangsadressebetegnelse;

    const postal = houseNumber.postnummer || {};
    const point = houseNumber.adgangspunkt && houseNumber.adgangspunkt.koordinater;
    const coordinates = point ? utm32ToWgs84(point.x, point.y) : null;

    return {
        address: stripPostalTail(designation),
        zip: postal.postnr || null,
        city: postal.navn || null,
        lon: coordinates ? coordinates[0] : null,
        lat: coordinates ? coordinates[1] : null
    };
}

async function fetchWithTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

// Throws only when the service itself is unusable - transport failure, timeout,
// or a 5xx that survived its retry. Those are what drop the form into degraded
// entry, so a 404 or an error status must not throw.
async function requestJson(url, { timeoutMs, retries }) {
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
        let response;
        try {
            response = await fetchWithTimeout(url, timeoutMs);
        } catch (error) {
            lastError = error;
            continue;
        }

        if (response.status === 404) return { notFound: true, body: null };

        if (response.status >= 500) {
            lastError = new Error(`Adressevaelger responded ${response.status}`);
            continue;
        }

        if (!response.ok) throw new Error(`Adressevaelger responded ${response.status}`);

        return { notFound: false, body: await response.json() };
    }

    throw lastError;
}

function createClient(options = {}) {
    const token = options.token;
    if (!token) throw new Error("Adressevaelger client requires a token");

    const baseUrl = (options.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, "");
    const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
    const retries = options.retries == null ? DEFAULT_RETRIES : options.retries;

    function url(path, params) {
        const query = Object.keys(params)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join("&");
        return `${baseUrl}${path}?${query}`;
    }

    // An empty array means "no matches" - a normal answer, not a failure.
    async function search(text) {
        const query = normalizeQuery(text);
        if (!query) return [];

        const { body } = await requestJson(url("/adresser/soeg", { tekst: query, token }), {
            timeoutMs,
            retries
        });

        if (!body || body.status === "fejl") return [];
        return body.fund || [];
    }

    // null when this particular address cannot be resolved; house numbers with
    // DAR status 4-5 are known to 404.
    async function lookup(type, id) {
        const resource = RESOURCE_BY_TYPE[type];
        if (!resource || !id) return null;

        const { notFound, body } = await requestJson(url(`/${resource}/${encodeURIComponent(id)}`, { token }), {
            timeoutMs,
            retries
        });

        if (notFound || !body || body.status === "fejl") return null;
        return mapAddress(body);
    }

    return { search, lookup, baseUrl };
}

export {
    createClient,
    mapAddress,
    parseDesignation,
    stripPostalTail,
    isTerminalRow,
    isRefinementRow,
    RESOURCE_BY_TYPE,
    REFINEMENT_TYPES
};
